import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PermissoesProvider, usePermissoes } from './contexts/PermissoesContext';
import { TabProvider } from './contexts/TabContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import PDV from './pages/PDV';
import PesagemEtiquetas from './pages/PesagemEtiquetas';
import Loja from './pages/Loja';
import LoginComprador from './pages/loja/LoginComprador';
import AssistirCurso from './pages/loja/AssistirCurso';
import RelatoriosMobile from './pages/RelatoriosMobile';
import MesasMobile from './pages/MesasMobile';
import FilaProducao from './pages/FilaProducao';
import useIsMobile from './hooks/useIsMobile';
import { modoMobileCompleto, hubMobileVisto } from './utils/modoMobileCompleto';

// Carregado sob demanda: a biblioteca de leitura de código de barras pela
// câmera (html5-qrcode) pesa +100KB gzip — só quem realmente entra nesta
// tela (Estoquista/Repositor) deveria pagar esse custo, não todo mundo que
// abre o sistema (PDV, telas desktop etc. carregariam esse peso à toa se
// fosse import direto).
const ReposicaoMobile = React.lazy(() => import('./pages/ReposicaoMobile'));
const PdvMobile = React.lazy(() => import('./pages/PdvMobile'));
const PedidoMobile = React.lazy(() => import('./pages/PedidoMobile'));
const PainelMobile = React.lazy(() => import('./pages/PainelMobile'));
const MobileInicio = React.lazy(() => import('./pages/MobileInicio'));

function RotaProtegida({ children }) {
  const { token } = useAuth();
  return token ? children : <Navigate to="/login" replace />;
}

// Bloqueia acesso por URL direta a telas fora do perfil (o PDV fica fora do
// Layout/TabWatcher, então precisa da própria guarda)
function ExigeAcesso({ path, children }) {
  const { pronto, permitido, rotaInicial } = usePermissoes();
  if (!pronto) return null;
  if (permitido(path)) return children;
  return <Navigate to={rotaInicial()} replace />;
}

// O sistema é desktop-first (sidebar + abas) — em celular, em vez de
// espremer telas que não foram desenhadas pra tela pequena, manda direto
// pra uma versão simplificada. Usuário Garçom/Comanda vai pra Mesas/
// Comandas (é o trabalho dele o dia inteiro — "Comanda" é a mesma tela,
// só um papel de login separado do Garçom); Fila de Produção vai pra tela
// da cozinha; os demais vão pra Relatórios (única outra tela pensada pra
// mobile). Não se aplica ao /login nem à vitrine pública (/loja/*), que já
// são (ou precisam ser) acessíveis normalmente pelo celular.
function GuardaMobile({ children }) {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { usuario } = useAuth();
  // suporterb nos sites de gestão navega por tudo (menu lateral em gaveta);
  // só passa primeiro pelo menu inicial mobile ao abrir o sistema.
  if (isMobile && modoMobileCompleto(usuario)) {
    return hubMobileVisto() ? children : <Navigate to="/mobile" replace />;
  }
  const destinoMobile = usuario?.fila_producao ? '/fila-producao'
    : (usuario?.garcom || usuario?.comanda) ? '/mesas-mobile'
    : usuario?.pdv_mobile ? '/pdv-mobile'
    : usuario?.pedido_mobile ? '/pedidos-mobile'
    : usuario?.estoquista ? '/reposicao-mobile'
    : '/relatorios-mobile';
  if (isMobile && location.pathname !== destinoMobile) {
    return <Navigate to={destinoMobile} replace />;
  }
  return children;
}

export default function App() {

  return (
    <AuthProvider>
      <PermissoesProvider>
        <BrowserRouter>
          <TabProvider>
            <Routes>
              <Route path="/login" element={<Login />} />

              {/* Painel de Clientes deixou de ser rota separada — agora é
                  uma aba normal dentro do Layout (TabContext#ROTAS), junto
                  de Produtos/Clientes/Contas a Receber etc. Continua com
                  login PRÓPRIO (painel_usuarios) dentro da aba — entrar no
                  ERP não loga automaticamente ali, são credenciais
                  diferentes. Visível só em rbdigital/jcr (ver o atalho
                  condicional em Sidebar.jsx); o backend segue sendo quem
                  garante de verdade (404 em qualquer outro host, mesmo se
                  alguém digitar a URL manualmente). */}

              {/* Vitrine pública do catálogo — sem auth, visitante anônimo */}
              <Route path="/loja/:slug" element={<Loja />} />
              <Route path="/loja/:slug/entrar" element={<LoginComprador />} />
              <Route path="/loja/:slug/assistir/:produtoId" element={<AssistirCurso />} />

              {/* Menu inicial mobile do suporterb nos sites de gestão */}
              <Route path="/mobile" element={
                <RotaProtegida>
                  <React.Suspense fallback={null}><MobileInicio /></React.Suspense>
                </RotaProtegida>
              } />

              {/* Painel de Clientes no celular — só suporterb, só nos sites
                  de gestão (a própria tela confere). */}
              <Route path="/painel-mobile" element={
                <RotaProtegida>
                  <React.Suspense fallback={null}><PainelMobile /></React.Suspense>
                </RotaProtegida>
              } />

              {/* Versão simplificada de Relatórios pra acesso pelo celular —
                  não passa pela GuardaMobile (é o próprio destino dela) */}
              <Route path="/relatorios-mobile" element={
                <RotaProtegida><RelatoriosMobile /></RotaProtegida>
              } />

              {/* Versão simplificada de Mesas/Comandas pra acesso pelo
                  celular (usuário Garçom) — não passa pela GuardaMobile
                  (é o próprio destino dela) */}
              <Route path="/mesas-mobile" element={
                <RotaProtegida><MesasMobile /></RotaProtegida>
              } />

              {/* Fila de Produção (tela da cozinha) — usuário com a flag
                  usuarios.fila_producao cai direto aqui (celular, tablet ou
                  desktop — não é uma tela "mobile" no sentido estreito,
                  é o próprio quiosque da cozinha). Não passa pela
                  GuardaMobile (é o próprio destino dela). */}
              <Route path="/fila-producao" element={
                <RotaProtegida><FilaProducao /></RotaProtegida>
              } />

              {/* Reposição de Mercadoria pra acesso pelo celular (usuário
                  Estoquista/Repositor) — não passa pela GuardaMobile (é o
                  próprio destino dela) */}
              <Route path="/reposicao-mobile" element={
                <RotaProtegida>
                  <React.Suspense fallback={null}><ReposicaoMobile /></React.Suspense>
                </RotaProtegida>
              } />

              {/* Pedidos/Orçamentos simplificado pra celular (usuário Pedido
                  Mobile) — não passa pela GuardaMobile (é o próprio destino dela) */}
              <Route path="/pedidos-mobile" element={
                <RotaProtegida>
                  <React.Suspense fallback={null}><PedidoMobile /></React.Suspense>
                </RotaProtegida>
              } />

              {/* PDV simplificado pra celular (usuário PDV Mobile) — não
                  passa pela GuardaMobile (é o próprio destino dela) */}
              <Route path="/pdv-mobile" element={
                <RotaProtegida>
                  <React.Suspense fallback={null}><PdvMobile /></React.Suspense>
                </RotaProtegida>
              } />

              {/* PDV — tela cheia sem sidebar. NÃO passa pela GuardaMobile: o
                  PDV já é sua própria tela cheia (sem sidebar/abas), feita
                  pra caber em qualquer resolução de caixa — uma janela do
                  navegador não maximizada no PC do caixa (largura < 768px)
                  bounçava pra Relatórios Mobile bem no meio da venda, o que
                  não faz sentido nenhum aqui (mesmo padrão de exceção já
                  usado por /relatorios-mobile, /reposicao-mobile e
                  /mesas-mobile — só que ali é o destino em si). */}
              <Route path="/vendas/pdv" element={
                <RotaProtegida>
                  <ExigeAcesso path="/vendas/pdv"><PDV /></ExigeAcesso>
                </RotaProtegida>
              } />

              {/* Pesagem/Etiquetas — guia própria, como o PDV (módulo
                  liberado por organização: pesagem_etiquetas) */}
              <Route path="/vendas/pesagem" element={
                <RotaProtegida>
                  <ExigeAcesso path="/vendas/pesagem"><PesagemEtiquetas /></ExigeAcesso>
                </RotaProtegida>
              } />

              {/* Todas as demais rotas → Layout com abas */}
              <Route
                path="/*"
                element={
                  <RotaProtegida>
                    <GuardaMobile>
                      <Layout />
                    </GuardaMobile>
                  </RotaProtegida>
                }
              />
            </Routes>
          </TabProvider>
        </BrowserRouter>
      </PermissoesProvider>
    </AuthProvider>
  );
}
